import _ from 'lodash';
import $ from 'jquery';
import React from 'react';
import ReactDOM from 'react-dom';
import { ForgeIcon, ForgeFloatingActionButton } from '@tylertech/forge-react';

import { FeatureFlags } from '@socrata/platform/feature-flags';
import { I18n } from 'common/i18n';
import { ScrollToTop } from 'common/components/ScrollToTop/ScrollToTop';
import { addPresentationModeAnchors } from '../lib/TableOfContentsUtils';
import { inPrintMode } from 'util/in-print-mode';
import 'common/a11y/screen-reader-only/index.scss';

interface SearchParameters {
  [key: string]: boolean;
}

/**
 * @class PresentationMode
 * @description
 * Adds functionality to the presentation mode template.
 * This includes:
 * - Taking a preview-mode story and showing blocks one at a time.
 * - Paging between individual blocks
 * - Resizing the content to fit appropriately into the screen size.
 * - When not presenting:
 *   - shows a scroll to top button
 *   - shows a print button which opens a ?print=true tab
 */
export default function PresentationMode(): void {
  const renderScrollToTopButton = (): JSX.Element => {
    const minPageScrolls = 3;
    const offsetHeight = window.innerHeight * minPageScrolls;
    return <ScrollToTop floatingActionButtonProps={{ mini: true }} pageYOffsetThreshold={offsetHeight} />;
  };

  const renderPrintFromViewButton = (): JSX.Element | null => {
    const alreadyPrinting = inPrintMode();
    const printFromViewEnabled = FeatureFlags.valueOrDefault('enable_print_from_view_story', true);
    // Skip print button if we're on ?print=true, or not on FF
    if (alreadyPrinting || !printFromViewEnabled) {
      return null;
    }

    const printUrl = `${window.location.href}?print=true`;
    return (
      <div className="btn-print-from-view">
        <ForgeFloatingActionButton
          mini
          name={I18n.t('preview_mode.print_from_view')}
          aria-label={I18n.t('preview_mode.print_from_view')}
          onClick={() => window.open(printUrl)}
        >
          <ForgeIcon name="print" aria-hidden="true"></ForgeIcon>
        </ForgeFloatingActionButton>
      </div>
    );
  };

  const renderPresentationModeButton = (): JSX.Element => {
    return (
      <div className="btn-presentation-mode">
        <ForgeFloatingActionButton
          mini
          name={I18n.t('preview_mode.presentation_mode.start')}
          aria-label={I18n.t('preview_mode.presentation_mode.start')}
          onClick={togglePresentationMode}
        >
          <ForgeIcon
            name="presentation_play"
            aria-hidden="true"
            external
            external-type="extended"
          ></ForgeIcon>
        </ForgeFloatingActionButton>
      </div>
    );
  };

  const renderPresentationControls = (): JSX.Element => {
    return (
      // Presentation controls start hidden, revealed in togglePresentationMode
      <div className="presentation-controls hidden">
        <div className="btn-presentation-previous">
          <ForgeFloatingActionButton
            mini
            name={I18n.t('preview_mode.presentation_mode.previous')}
            onClick={pagePrevious}
            aria-label={I18n.t('preview_mode.presentation_mode.previous')}
          >
            <ForgeIcon name="chevron_left" aria-hidden="true"></ForgeIcon>
          </ForgeFloatingActionButton>
        </div>
        <div className="btn-presentation-next">
          <ForgeFloatingActionButton
            mini
            name={I18n.t('preview_mode.presentation_mode.next')}
            onClick={pageNext}
            aria-label={I18n.t('preview_mode.presentation_mode.next')}
          >
            <ForgeIcon name="chevron_right" aria-hidden="true"></ForgeIcon>
          </ForgeFloatingActionButton>
        </div>
        <div className="btn-presentation-close">
          <ForgeFloatingActionButton
            mini
            name={I18n.t('preview_mode.presentation_mode.exit')}
            onClick={togglePresentationMode}
            aria-label={I18n.t('preview_mode.presentation_mode.exit')}
          >
            <ForgeIcon name="close" aria-hidden="true"></ForgeIcon>
          </ForgeFloatingActionButton>
        </div>
      </div>
    );
  };

  const renderPersonalizedResetButtonContainer = (): JSX.Element | undefined => {
    // We render a div here so we can attach our react component in view/index
    // with the StoryReduxStore
    if (FeatureFlags.valueOrDefault('enable_saved_state_in_stories', false)) {
      return <div className="btn-reset-personalization" />;
    }
  };

  const renderWatchAssetButton = (): JSX.Element | undefined => {
    if (
      FeatureFlags.valueOrDefault('enable_screenshot_subscriptions', false) &&
      FeatureFlags.valueOrDefault('enable_embedded_schedule_send', false) &&
      !FeatureFlags.valueOrDefault('enable_report_scheduled_delivery', false)
    ) {
      return <div className="btn-watch-asset-button" data-testid="btn-watch-asset-button" />;
    }
  };

  const renderScheduleDeliveryButton = (): JSX.Element | undefined => {
    if (
      FeatureFlags.valueOrDefault('enable_report_scheduled_delivery', false) &&
      FeatureFlags.valueOrDefault('enable_embedded_schedule_send', false)
    ) {
      return <div id="btn-send-scheduled-report" data-testid="btn-send-scheduled-report" />;
    }
  };

  ReactDOM.render(
    <>
      <div className="viewing-controls">
        {renderScrollToTopButton()}
        {renderPersonalizedResetButtonContainer()}
        {renderPrintFromViewButton()}
        {renderWatchAssetButton()}
        {renderScheduleDeliveryButton()}
        {FeatureFlags.valueOrDefault('enable_present_in_stories', true)
          ? renderPresentationModeButton()
          : null}
      </div>
      {renderPresentationControls()}
    </>,
    document.querySelector('.actions-bar'),
  );

  const blocks = Array.from(document.querySelectorAll('.block')) as HTMLElement[];
  const userStory = document.querySelector('.user-story') as HTMLElement;
  const footer = document.querySelector('#site-chrome-footer') as HTMLElement;
  const viewingControls = document.querySelector('.viewing-controls') as HTMLElement;
  const presentationControls = document.querySelector('.presentation-controls') as HTMLElement;

  document.documentElement.addEventListener('keyup', pageOrClose);

  attachPageIndexes();
  presentOnStart();

  function attachPageIndexes(): void {
    let index = 0;

    blocks.forEach(function (block) {
      if (notBlacklisted(block)) {
        block.setAttribute('data-page-index', (index++).toString());
      }
    });

    if (userStory.querySelectorAll('.component-html-table-of-contents').length > 0) {
      addPresentationModeAnchors(blocks);
    }
  }

  function removeContentLinkEventHandler(): void {
    _.each(
      // IE doesn't implement forEach on NodeLists.
      userStory.querySelectorAll('.component-html-table-of-contents a'),
      (contentLink) => contentLink.removeEventListener('click', skipToAnchor),
    );
  }

  function presentOnStart(): void {
    const searchParameters: SearchParameters = {};
    const search =
      window.location.search[0] === '?' ? window.location.search.slice(1) : window.location.search;

    const keyValues = _.compact(search.split('&'));

    _.each(keyValues, (keyValue) => {
      const [key, value] = keyValue.split('=');
      searchParameters[key] = value !== 'false';
    });

    // Don't steal focus from the page when it loads already presenting
    if (searchParameters.present) {
      enterPresentationMode({ moveFocus: false });
    }
  }

  function togglePresentationMode(): void {
    if (userStory.classList.contains('presentation-mode')) {
      enableLinearMode();
    } else {
      enterPresentationMode({ moveFocus: true });
    }
  }

  function enterPresentationMode({ moveFocus }: { moveFocus: boolean }): void {
    _.each(
      // IE doesn't implement forEach on NodeLists.
      userStory.querySelectorAll('.component-html-table-of-contents a'),
      (contentLink) => contentLink.addEventListener('click', skipToAnchor),
    );

    // Re-evaluate which blocks should be presentable when entering presentation mode
    attachPageIndexes();

    userStory.classList.add('presentation-mode');
    footer.classList.add('presentation-mode');
    viewingControls.classList.add('hidden');
    presentationControls.classList.remove('hidden');

    blocks.forEach(function (block) {
      const pageIndexAttr = block.getAttribute('data-page-index');
      if (pageIndexAttr === null) {
        // Block has no page index, meaning it's not presentable - hide it
        block.classList.add('hidden');
      } else {
        const index = parseInt(pageIndexAttr, 10);
        if (index !== 0) {
          block.classList.add('hidden');
        } else {
          block.classList.remove('hidden');
        }
      }
    });

    const firstSlide = document.querySelector('.block[data-page-index="0"]') as HTMLElement | null;
    if (firstSlide) {
      labelSlide(firstSlide);
      if (moveFocus) {
        firstSlide.focus({ preventScroll: true });
      } else {
        // Focus stays where the page put it, so the slide has to be reachable by Tab to be announced
        firstSlide.setAttribute('tabindex', '0');
      }
    }

    screenReaderAnnouncement(I18n.t('preview_mode.presentation_mode.started'));
  }

  function enableLinearMode(): void {
    userStory.classList.remove('presentation-mode');
    footer.classList.remove('presentation-mode');
    viewingControls.classList.remove('hidden');
    presentationControls.classList.add('hidden');

    blocks.forEach(function (block) {
      block.classList.remove('hidden');
      unlabelSlide(block);
    });

    removeContentLinkEventHandler();
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // The slide that had focus is no longer a landmark, so hand focus back to the Start button
    const startButton = document.querySelector('.btn-presentation-mode forge-fab');
    (startButton as HTMLElement | null)?.focus();
    screenReaderAnnouncement(I18n.t('preview_mode.presentation_mode.ended'));

    $('.socrata-visualization').trigger('SOCRATA_VISUALIZATION_INVALIDATE_SIZE');
  }

  function labelSlide(block: HTMLElement): void {
    const current = parseInt(block.getAttribute('data-page-index') || '0', 10) + 1;
    const total = document.querySelectorAll('.block[data-page-index]').length;
    const title = block.querySelector('h1, h2, h3, h4, h5, h6')?.textContent?.trim();

    const label = title
      ? I18n.t('preview_mode.presentation_mode.slide_label_with_title', { current, total, title })
      : I18n.t('preview_mode.presentation_mode.slide_label', { current, total });

    block.setAttribute('tabindex', '-1');
    block.setAttribute('role', 'group');
    block.setAttribute('aria-label', label);
  }

  function unlabelSlide(block: HTMLElement): void {
    block.removeAttribute('tabindex');
    block.removeAttribute('role');
    block.removeAttribute('aria-label');
  }

  function getAnnouncer(): HTMLElement {
    const existing = document.getElementById('presentation-mode-announcer');
    if (existing) {
      return existing;
    }

    const announcer = document.createElement('div');
    announcer.setAttribute('id', 'presentation-mode-announcer');
    announcer.setAttribute('aria-live', 'polite');
    announcer.classList.add('screenreader-only');
    document.body.appendChild(announcer);
    return announcer;
  }

  function screenReaderAnnouncement(text: string): void {
    getAnnouncer().textContent = text;
  }

  function switchToPage(newPageIndex: number, defaultPageIndex?: number): void {
    const visible = document.querySelector('.block:not(.hidden)') as HTMLElement;
    let newVisible = document.querySelector(`.block[data-page-index="${newPageIndex}"]`) as HTMLElement;

    if (defaultPageIndex !== undefined) {
      newVisible =
        newVisible ||
        (document.querySelector(`.block[data-page-index="${defaultPageIndex}"]`) as HTMLElement);
    }
    if (!newVisible) {
      return;
    }

    visible.classList.add('hidden');
    unlabelSlide(visible);
    newVisible.classList.remove('hidden');

    // Focus moves to the new slide so its label is announced and screen reader users land on the content
    labelSlide(newVisible);
    newVisible.focus({ preventScroll: true });

    // Re-render any visualizations in the newly visible page
    $(newVisible).find('.socrata-visualization').trigger('SOCRATA_VISUALIZATION_INVALIDATE_SIZE');
  }

  function pageNext(): void {
    const visible = document.querySelector('.block:not(.hidden)') as HTMLElement;
    const nextIndex = parseInt(visible.getAttribute('data-page-index') || '0', 10) + 1;
    switchToPage(nextIndex, 0);
  }

  function pagePrevious(): void {
    const visible = document.querySelector('.block:not(.hidden)') as HTMLElement;
    const previousIndex = parseInt(visible.getAttribute('data-page-index') || '0', 10) - 1;

    const pageable = document.querySelectorAll('.block[data-page-index]');
    switchToPage(previousIndex, pageable.length - 1);
  }

  function pageOrClose(event: KeyboardEvent): void {
    const key = event.charCode || event.keyCode;
    const isPresenting = document.querySelector('.presentation-mode');

    if (isPresenting) {
      // 27 == ESC, 37 == <-, 39 == ->
      // 33 = PageUp, 34 = PageDn
      if (key === 27) {
        enableLinearMode();
      } else if (key === 37 || key === 33) {
        pagePrevious();
      } else if (key === 39 || key === 34) {
        pageNext();
      }
    }
  }

  function skipToAnchor(this: HTMLAnchorElement): void {
    const destination = _.last(this.href.split('#'));
    if (destination) {
      const destBlock = userStory.querySelector(`[data-heading-ids*=",${destination},"]`) as HTMLElement;
      if (destBlock) {
        const destIndex = parseInt(destBlock.getAttribute('data-page-index') || '0', 10);
        switchToPage(destIndex);
      }
    }
  }

  function notBlacklisted(element: HTMLElement): boolean {
    return element.dataset.hasOwnProperty('presentable') && element.dataset.presentable !== 'false';
  }
}
